import React from "react";
import {styled} from "@reside/ui";
import {TableUserDto, UserPermissionEnum} from "../../../../utils/types/user";
import {RoleNameEnum} from "../../../../utils/types/common";

import {
  CheckboxesTypes,
  GeneralTypes,
  OtherTypes,
} from "../permission-management-modal/permissionTypes";
import {AdminRoles} from "../../../../atoms/admin-roles";
import {BulkPermissionsMacroCheckbox} from "./BulkPermissionsMacroCheckbox";
import {BulkQuicksightDashboardsCheckbox} from "./BulkQuicksightDashboardsCheckbox";
import {PermissionsBulkStatusLegend} from "./PermissionsBulkStatusLegend";

type Props = Readonly<{
  users: ReadonlyArray<TableUserDto>;
  userIds: ReadonlyArray<string>;
  hasPermission: (userId: string, permission: UserPermissionEnum) => boolean;
  isPermissionPending: (permission: UserPermissionEnum) => boolean;
  onTogglePermission: (
    permission: UserPermissionEnum,
    grantToAll: boolean,
  ) => void;
  onToggleCategory: (
    permissions: ReadonlyArray<UserPermissionEnum>,
    grantToAll: boolean,
  ) => void;
  dashboardOptions: ReadonlyArray<{id?: string; name?: string}>;
  isQuicksightLoading: boolean;
  isQuicksightError: boolean;
  isQuicksightSaving: boolean;
  hasDashboard: (userId: string, dashboardId: string) => boolean;
  isDashboardPending: (dashboardId: string) => boolean;
  onToggleDashboard: (dashboardId: string, grantToAll: boolean) => void;
  onToggleAllDashboards: (grantToAll: boolean) => void;
}>;

export const BulkPermissionManagementForm = ({
  users,
  userIds,
  hasPermission,
  isPermissionPending,
  onTogglePermission,
  onToggleCategory,
  dashboardOptions,
  isQuicksightLoading,
  isQuicksightError,
  isQuicksightSaving,
  hasDashboard,
  isDashboardPending,
  onToggleDashboard,
  onToggleAllDashboards,
}: Props) => (
  <>
    <PermissionsBulkStatusLegend />
    <FormGrid>
      <Column>
        <BulkPermissionsMacroCheckbox
          label="General"
          permissions={GeneralTypes}
          userIds={userIds}
          hasPermission={hasPermission}
          isPermissionPending={isPermissionPending}
          onTogglePermission={onTogglePermission}
          onToggleCategory={onToggleCategory}
        />
        <BulkPermissionsMacroCheckbox
          label="Checkboxes"
          permissions={CheckboxesTypes}
          userIds={userIds}
          hasPermission={hasPermission}
          isPermissionPending={isPermissionPending}
          onTogglePermission={onTogglePermission}
          onToggleCategory={onToggleCategory}
        />
        <AdminRoles roles={[RoleNameEnum.RESIDE_ADMIN]}>
          <BulkPermissionsMacroCheckbox
            label="Other"
            permissions={OtherTypes}
            userIds={userIds}
            hasPermission={hasPermission}
            isPermissionPending={isPermissionPending}
            onTogglePermission={onTogglePermission}
            onToggleCategory={onToggleCategory}
          />
        </AdminRoles>
      </Column>
      <Column>
        <BulkQuicksightDashboardsCheckbox
          users={users}
          dashboardOptions={dashboardOptions}
          isLoading={isQuicksightLoading}
          isError={isQuicksightError}
          isSaving={isQuicksightSaving}
          hasDashboard={hasDashboard}
          isDashboardPending={isDashboardPending}
          onToggleDashboard={onToggleDashboard}
          onToggleAllDashboards={onToggleAllDashboards}
        />
      </Column>
    </FormGrid>
  </>
);

const FormGrid = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 24px;
`;

const Column = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;
